import { SearchableSelect, SearchableSelectOption } from "@comps/ui/searchable-select";
import Field, { FieldType, aggrFilterLabel, columnAggregation, fieldToOutputType } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { FILTERABLE_TYPES, Filter, Operator } from "@dto/records";
import { getFieldKey, getPathKeys, getValidOperator } from "@utils/model";
import { TW_FilterEditorNewFilter, TW_FilterEditorPathSelect, TW_FilterEditorRemoveFilter, TW_FilterEditorRemoveFilterDisabled, useIconHover } from "@utils/twClasses";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";
import { ChoiceFilterValueSelector } from "./ChoiceFilterValueSelector";
import { DateFilterValueEditor } from "./DateFilterValueEditor";
import { PeriodFilterValueEditor } from "./PeriodFilterValueEditor";
import { PrimitiveFilterValueSelector } from "./PrimitiveFilterValueSelector";
import { RecordFilterValueSelector } from "./RecordFilterValueSelector";
import { UserFilterValueSelector } from "./UserFilterValueSelector";
import { SOURCE_SEPARATOR } from "@utils/constants";
import { hasPeriodValueFilter } from "./periodFilterLogic";

interface FilterItemProps {
    filter: Filter | null,
    objectInfos: ObjectInfos,
    onFilterChange: (filter: Filter) => void,
    onFilterRemove: (pathKey: string) => void,
    usedFilterPathKeys: string[],
    disabled: boolean
}

function getOutputTypeByPathKey(pathKey: string, outputTypeByPathKeys: Map<string, FieldType>): FieldType | undefined {
    const outputType = outputTypeByPathKeys.get(pathKey);
    if (outputType) {
        return outputType;
    }

    if (pathKey && pathKey.startsWith(SOURCE_SEPARATOR)) {
        const sourcePathKey = Object.keys(outputTypeByPathKeys).find(key => {
            if (key.endsWith(pathKey)) {
                return true;
            }
            return false;
        });
        if (sourcePathKey) {
            return outputTypeByPathKeys.get(sourcePathKey);
        }
        return undefined;
    }
    return undefined;
}

function FilterItem({ filter, objectInfos, onFilterChange, onFilterRemove, usedFilterPathKeys, disabled }: FilterItemProps) {

    const { t } = useTranslation();
    const [selectedPathKey, setSelectedPathKey] = useState<string | undefined>(undefined);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const wsColor = useModelStore((state) => state.wsColor);
    const trashHover = useIconHover("#00000088", wsColor);
    const selectedView = useModelStore((state) => state.selectedView);
    const aggrByPathKeys = selectedView?.aggregations;

    useEffect(() => {
        if (filter) {

            const fieldFilter = filter.groupFilter;
            const filters = fieldFilter?.filters;
            if (filters && filters.length > 0) {
                const firstFilter = filters[0];
                const filterPathKey = firstFilter?.singleFilter?.pathKey;
                if (filterPathKey) {
                    let pathKeys = getPathKeys(objectInfos, fields, relationByKeys, fieldByKeys, true);
                    const selectedPathKeyIndex = pathKeys.findIndex(pathKey => pathKey === filterPathKey);
                    if (selectedPathKeyIndex > -1) {
                        setSelectedPathKey(filterPathKey);
                    } else {
                        // Keep the full pathKey even if not in local pathKeys list
                        // This handles relation filters with transformed paths
                        setSelectedPathKey(filterPathKey);
                    }
                }
            }
        } else {
            setSelectedPathKey(undefined);
        }
    }, [filter]);

    const fields = objectInfos?.fields;
    if (!fields) {
        return null;
    }
    let pathKeys = getPathKeys(objectInfos, fields, relationByKeys, fieldByKeys, false);
    const validOperatorsPerPathKeys = new Map<string, Operator[] | null>();
    const outputTypeByPathKeys = new Map<string, FieldType>();
    const fieldByPathKeys = new Map<string, Field>();
    pathKeys.forEach((pathKey, index) => {
        if (pathKey == null) {
            return
        }
        const field = fields[index];
        if (!field) {
            return;
        }
        fieldByPathKeys.set(pathKey!, field);
        const aggregation = columnAggregation(field, aggrByPathKeys, pathKey);
        let outputType = fieldToOutputType(field, aggregation, fieldByKeys, true);
        outputTypeByPathKeys.set(pathKey, outputType);
        const validOperators = getValidOperator(outputType, field.typeItems)
        if (!FILTERABLE_TYPES.includes(field.type)) {
            validOperatorsPerPathKeys.set(pathKey, null)
        } else {
            validOperatorsPerPathKeys.set(pathKey, validOperators)
        }
    })

    pathKeys = pathKeys.filter((pathKey) => {
        return validOperatorsPerPathKeys.get(pathKey!) !== null;
    });

    const onPathChange = (pathKey: string) => {
        if (pathKey !== selectedPathKey) {
            setSelectedPathKey(pathKey);
        }
    }

    const onRemoveFilter = () => {
        if (!selectedPathKey) {
            return;
        }

        if (!filter) {
            setSelectedPathKey(undefined);
            return;
        }

        onFilterRemove(selectedPathKey);
    }

    function validateFilter(filter: Filter | null) {
        if (!filter) {
            return;
        }
        onFilterChange(filter);
    }


    const fieldKey = selectedPathKey ? getFieldKey(selectedPathKey) : null;
    const field = fieldKey ? (fieldByKeys?.get(fieldKey.fieldKey) || null) : null;
    const isLink = fieldKey && fieldKey.objectKey !== objectInfos?.object?._key;
    const disabledTrash = !selectedPathKey;

    // Check if this is a relation filter (pathKey not in local pathKeys list)
    const isExternalFilter = selectedPathKey && !pathKeys.includes(selectedPathKey);

    const validOperators = validOperatorsPerPathKeys.get(selectedPathKey!) ?? getValidOperator(field?.type ?? FieldType.Text, field?.typeItems);
    const outputType = getOutputTypeByPathKey(selectedPathKey!, outputTypeByPathKeys) ?? field?.type;
    const selectedAggregation = columnAggregation(field, aggrByPathKeys, selectedPathKey);
    // A period value set while the column was aggregated by period stays
    // visible and editable once the aggregation is removed: the date editor
    // cannot display an IN filter value.
    const usePeriodEditor = outputType === FieldType.Period || (outputType === FieldType.Date && hasPeriodValueFilter(filter));
    return (
        <div className={TW_FilterEditorNewFilter} key={selectedPathKey ?? "new"}>
            {isExternalFilter ? (
                <div className={TW_FilterEditorPathSelect + " flex items-center px-3 bg-white h-9 text-sm text-gray-400 rounded-md border border-input"}>
                    {field?.name ?? selectedPathKey}
                </div>
            ) : (
                <SearchableSelect
                    className={TW_FilterEditorPathSelect}
                    placeholder={t("filter.path.placeholder")}
                    value={selectedPathKey}
                    onValueChange={onPathChange}
                    disabled={selectedPathKey !== undefined || disabled}
                    options={(pathKeys ?? []).flatMap((pathKey): SearchableSelectOption[] => {
                        const currentField = fieldByPathKeys.get(pathKey!);
                        if (!currentField || !FILTERABLE_TYPES.includes(currentField.type)) {
                            return [];
                        }
                        if (pathKey === null) {
                            return [{ value: currentField._key, label: currentField.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                        }
                        const disabled = usedFilterPathKeys.includes(pathKey);

                        const aggregation = columnAggregation(currentField, aggrByPathKeys, pathKey);
                        let aggrLabel = aggrFilterLabel(t, aggregation);
                        if (aggrLabel !== "") {
                            aggrLabel = " (" + aggrLabel + ")";
                        }
                        return [{ value: pathKey, label: currentField.name + aggrLabel, disabled: disabled }];
                    })}
                />
            )}
            {selectedPathKey && isLink && (outputType === FieldType.Text || outputType === FieldType.TextArea || outputType === FieldType.Url) &&
                <RecordFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={disabled} />
            }
            {outputType === FieldType.User &&
                <UserFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={disabled} />
            }
            {!isLink && (outputType === FieldType.Text || outputType === FieldType.TextArea || outputType === FieldType.Url) &&
                <PrimitiveFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={disabled} />
            }
            {(outputType === FieldType.Number || outputType === FieldType.Boolean || outputType === FieldType.Decimal || outputType === FieldType.Percent || outputType === FieldType.Devise) &&
                <PrimitiveFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={disabled} />
            }
            {(outputType === FieldType.Choice || outputType === FieldType.MultiChoice) &&
                <ChoiceFilterValueSelector selectedPathKey={selectedPathKey} filter={filter!} validOperators={validOperators} validateFilter={validateFilter} disabled={disabled} />
            }
            {(outputType === FieldType.Date) && !usePeriodEditor &&
                <DateFilterValueEditor selectedPathKey={selectedPathKey} filter={filter!} validateFilter={validateFilter} disabled={disabled} />
            }
            {usePeriodEditor &&
                <PeriodFilterValueEditor field={field} aggregation={selectedAggregation} selectedPathKey={selectedPathKey} filter={filter!} validateFilter={validateFilter} disabled={disabled} />
            }
            {!fieldKey &&
                <PrimitiveFilterValueSelector selectedPathKey={selectedPathKey} filter={null} validOperators={[]} validateFilter={validateFilter} disabled={disabled} />
            }
            {(disabledTrash || disabled) && <Trash2 className={TW_FilterEditorRemoveFilterDisabled} />}
            {!disabledTrash && !disabled && <Trash2 className={TW_FilterEditorRemoveFilter} onClick={onRemoveFilter} {...trashHover} />}
        </div>
    )
}

export default FilterItem;
